import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";

const moneyFormatter = (currency) => new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: String(currency || "CAD").toUpperCase(),
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const emptyDraft = () => ({
  type: "discount",
  description: "Customer Discount",
  calculationType: "fixed",
  value: "",
  taxTreatment: "reduce-taxable-subtotal",
  source: "manual",
});

export default function InvoicingCartAdjustments({
  cartWorkspace,
  currency = "CAD",
  editable = true,
  onSaved = null,
  dialogOpen = false,
  onDialogClose = null,
  initialAdjustment = null,
  onEditRequest = null,
}) {
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const formatMoney = useMemo(() => moneyFormatter(currency), [currency]);
  const adjustments = Array.isArray(cartWorkspace?.cartPayload?.Adjustments)
    ? cartWorkspace.cartPayload.Adjustments
    : [];
  const enabled = (
    cartWorkspace?.source === "invoicing-cart"
    && cartWorkspace?.capabilities?.adjustments === true
  );

  useEffect(() => {
    if (!dialogOpen) return;
    setDraft(initialAdjustment ? { ...initialAdjustment } : emptyDraft());
  }, [dialogOpen, initialAdjustment]);

  if (!enabled) return null;

  const persistMutation = async (mutationResult) => {
    if (!mutationResult?.ok || !mutationResult?.payload) {
      toast.error(mutationResult?.message || "Unable to update invoice adjustment.");
      return false;
    }
    setSaving(true);
    try {
      await cartWorkspace.savePayload(mutationResult.payload);
      await cartWorkspace.reload?.();
      await onSaved?.();
      return true;
    } catch (error) {
      toast.error(
        error?.response?.data?.message
        || error?.message
        || "Unable to save invoice adjustment."
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async () => {
    const value = Number(draft?.value);
    const description = String(draft?.description || "").trim();
    if (!description || !(value > 0)) {
      toast.error("Enter a description and a positive adjustment value.");
      return;
    }
    if (draft.calculationType === "percentage" && value > 100) {
      toast.error("Percentage discounts cannot exceed 100%.");
      return;
    }

    const adjustment = {
      ...draft,
      type: "discount",
      description,
      value,
      taxTreatment: "reduce-taxable-subtotal",
      source: draft?.source || "manual",
    };

    const result = adjustment.id
      ? cartWorkspace.mutationHandlers.editAdjustment(adjustment)
      : cartWorkspace.mutationHandlers.addAdjustment(adjustment);

    if (await persistMutation(result)) onDialogClose?.();
  };

  const removeAdjustment = async (adjustment) => {
    if (!window.confirm(`Remove ${adjustment?.description || "this adjustment"}?`)) return;
    await persistMutation(
      cartWorkspace.mutationHandlers.deleteAdjustment(adjustment)
    );
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: 1.5 }}
        data-testid="invoicing-cart-adjustments"
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Adjustments
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Invoice-only discounts. Adjustments reduce the taxable subtotal before tax is calculated.
          </Typography>
        </Box>

        {adjustments.length ? <Divider sx={{ my: 1.5 }} /> : null}

        <Stack spacing={1}>
          {adjustments.map((adjustment) => (
            <Stack
              key={adjustment.id}
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              gap={1}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {adjustment.description || "Customer Discount"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {adjustment.calculationType === "percentage"
                    ? `${adjustment.value}% discount · applied before tax`
                    : `${formatMoney.format(Number(adjustment.value) || 0)} fixed discount · applied before tax`}
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                {Number.isFinite(Number(adjustment.resolvedAmount)) ? (
                  <Typography variant="body2" color="error.main" sx={{ fontWeight: 800 }}>
                    {formatMoney.format(Number(adjustment.resolvedAmount))}
                  </Typography>
                ) : null}
                {editable ? (
                  <>
                    <Button size="small" onClick={() => onEditRequest?.(adjustment)} disabled={saving}>
                      Edit
                    </Button>
                    <Button size="small" color="error" onClick={() => removeAdjustment(adjustment)} disabled={saving}>
                      Remove
                    </Button>
                  </>
                ) : null}
              </Stack>
            </Stack>
          ))}
          {!adjustments.length ? (
            <Typography variant="body2" color="text.secondary">
              No invoice adjustments.
            </Typography>
          ) : null}
        </Stack>
      </Paper>

      <Dialog open={dialogOpen} onClose={saving ? undefined : onDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{initialAdjustment ? "Edit Adjustment" : "Add Adjustment"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              This adjustment applies only to this invoicing cart. It reduces the taxable subtotal before tax is calculated.
            </Typography>
            <TextField
              label="Description"
              value={draft.description || ""}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              inputProps={{ maxLength: 240 }}
              fullWidth
              autoFocus
            />
            <TextField
              select
              label="Method"
              value={draft.calculationType || "fixed"}
              onChange={(event) => setDraft({ ...draft, calculationType: event.target.value })}
              fullWidth
            >
              <MenuItem value="fixed">Fixed Amount</MenuItem>
              <MenuItem value="percentage">Percentage</MenuItem>
            </TextField>
            <TextField
              type="number"
              label={draft.calculationType === "percentage" ? "Percent" : "Amount"}
              value={draft.value}
              onChange={(event) => setDraft({ ...draft, value: event.target.value })}
              inputProps={{
                min: 0,
                max: draft.calculationType === "percentage" ? 100 : undefined,
                step: 0.01,
              }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onDialogClose} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={saveDraft} disabled={saving}>
            {saving ? "Saving…" : "Save Adjustment"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
